import React, { useEffect, useState, useRef } from 'react';
import './style.scss';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import Rodal from 'rodal';

import { capitalizeEachWord, lowerCaseEachWord, SEEDED_RANDOM_INTS } from '../../../utils';

const questionIcon = require('../../../assets/icons/question-icon.svg');
const redExclamationIcon = require('../../../assets/icons/red-exclamation.svg');

const CANDIDATE_OVERLAP_THRESHOLD = 0.08;

const CompareCandidates = (props) => {
  const {
    candidates = [],
    fetchRace,
    history,
    nationalTopics,
    selectedElection,
    selectedRace,
    setSelectedCandidate,
    flaggedTopicsForRace,
    fetchAllTopicsForRace,
    createFlaggedTopic,
    match: {
      params: {
        id: raceId,
      },
    },
    setRaceTopicMeaning,
    user,
  } = props;

  const [overlapContainer, setOverlapContainer] = useState();
  const [mouseInOverlapContainer, setMouseInOverlapContainer] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [flaggedModalVisible, setFlaggedModalVisible] = useState(false);
  const [currFlaggedTopic, setCurrFlaggedTopic] = useState();
  const [currFlaggedTopicReason, setCurrFlaggedTopicReason] = useState('');
  const [currFlaggedTopicMeaning, setCurrFlaggedTopicMeaning] = useState('');

  const mouseInOverlapContainerRef = useRef(mouseInOverlapContainer);
  mouseInOverlapContainerRef.current = mouseInOverlapContainer;

  const overlapContainerRef = useRef(overlapContainer);
  overlapContainerRef.current = overlapContainer;

  useEffect(() => {
    if (!selectedElection && !selectedRace) {
      fetchRace(raceId);
    }
  }, [selectedElection, selectedRace]);

  useEffect(() => {
    if (!flaggedTopicsForRace && raceId) {
      fetchAllTopicsForRace(raceId);
    }
  }, [flaggedTopicsForRace, raceId]);

  if (!selectedElection || !selectedRace) {
    return null;
  }

  const {
    topic_stances: {
      scores = {},
    } = {},
  } = selectedRace || {};

  const {
    national_stances: userNationalStances = {},
    race_stances: {
      [raceId]: userRaceStances = {},
    } = {},
  } = user;

  const natlTopicNames = nationalTopics.map(t => t.title);
  const otherTopics = Object.keys(scores).filter(topic => !natlTopicNames.includes(topic));

  const seededRandomValsByCandidate = candidates
    .sort((a, b) => (a.name > b.name ? 1 : -1))
    .reduce((acc, curr, idx) => ({
      ...acc,
      [curr.name]: SEEDED_RANDOM_INTS[idx],
    }), {});

  const candidateStances = candidates.map((candidate) => {
    const {
      name,
      votesmart_candidate: {
        bio: {
          candidate: {
            photo,
          } = {},
        } = {},
      } = {},
      _id: candidateId,
    } = candidate;

    const splitName = name.split(' ');

    const firstInitial = splitName[0].substring(0, 1);
    const lastInitial = splitName[splitName.length - 1]?.substring(0, 1);

    const {
      topic_stances: {
        scores: candidateScores = {},
      } = {},
    } = selectedRace || {};

    const topicValues = Object.entries(candidateScores).reduce((acc, [title, obj]) => (
      { ...acc, [title]: obj[candidateId] }
    ), {});

    const initials = `${firstInitial}${lastInitial}`;

    return {
      candidateId,
      name,
      photo,
      candidatePreview: photo ? <img className="profile-photo" src={photo} alt={name} /> : <div className="profile-photo-empty">{initials}</div>,
      randomJiggle: seededRandomValsByCandidate[name],
      ...topicValues,
    };
  });

  // determine national topics that we have at least one entry for
  const nationalTopicValues = nationalTopics.reduce((acc, { description, title }) => {
    const values = candidateStances
      .map(candidate => candidate[title])
      .filter(val => !!val);

    return {
      ...acc,
      ...values.length > 0 ? { [description]: title } : {},
    };
  }, {});

  const onCandidateHoverStart = (candidate, field, title) => {
    // include user values in overlap if they took the quiz
    const userValues = {
      ...userNationalStances,
      ...userRaceStances,
      candidateId: 'user',
      candidatePreview: <div className="user-topic-answer" data-tip="Where you rated yourself in our quiz">YOU</div>,
    };

    const overlappingCandidates = [...candidateStances, userValues].reduce((acc, curr) => (
      [...acc, ...Math.abs(curr[field || title] - candidate[field || title]) < CANDIDATE_OVERLAP_THRESHOLD ? [curr] : []]
    ), []);

    if (overlappingCandidates.length > 1) {
      const currField = overlapContainer?.field;
      const currTitle = overlapContainer?.title;
      const currCandidates = (overlapContainer?.candidates || []).map(c => c.candidateId);

      if (currField !== field || currTitle !== title || currCandidates !== overlappingCandidates.map(c => c.candidateId)) {
        setOverlapContainer({
          field,
          title,
          candidates: overlappingCandidates,
          triggeredBy: candidate.candidateId,
        });
      }
    }
  };

  const onCandidateHoverStop = () => {
    const currentCandidateHover = overlapContainerRef.current?.triggeredBy;

    setTimeout(() => {
      const differentCandidates = overlapContainerRef.current?.triggeredBy !== currentCandidateHover;
      if (!mouseInOverlapContainerRef.current && !differentCandidates) setOverlapContainer();
    }, 0);
  };

  const renderCandidateOverlap = (candidate, field, title) => {
    const overlappingCandidates = candidateStances.reduce((acc, curr) => (
      [...acc, ...Math.abs(curr[field || title] - candidate[field || title]) < CANDIDATE_OVERLAP_THRESHOLD ? [curr] : []]
    ), []);

    if (overlappingCandidates.length > 1) return candidate.candidatePreview;
    else return null;
  };

  const generateCandidatePosition = (baseValue, jiggle) => {
    const posVal = 47 + ((baseValue + (jiggle || 0)) * 47);
    const negVal = 47 + ((baseValue + ((jiggle || 0) * -1)) * 47);
    return posVal > 90 ? negVal : posVal;
  };

  const submitFlaggedTopic = () => {
    createFlaggedTopic({ topic: currFlaggedTopic, reason: currFlaggedTopicReason, race: raceId });
    setModalVisible(false);
    setCurrFlaggedTopicReason('');
    setCurrFlaggedTopic();
  };

  const submitFlaggedTopicMeaning = () => {
    setRaceTopicMeaning(raceId, currFlaggedTopic, currFlaggedTopicMeaning);
    setModalVisible(false);
    setCurrFlaggedTopicMeaning('');
    setCurrFlaggedTopic();
  };

  const flaggedTopics = flaggedTopicsForRace ? flaggedTopicsForRace.map(topic => topic.topic) : [];

  const topicMeanings = selectedRace?.topic_meanings || [];

  return (
    <div id="compare-candidate-container">
      <h1>{selectedRace?.title}</h1>
      <h3>{selectedElection?.title}</h3>
      <h4>{moment(selectedElection.date).format('MMMM Do, YYYY')}</h4>

      {Object.keys(nationalTopicValues).length > 0 ? (
        <div className="candidate-natl-topic-container content-container">
          <div className="top-info-container">
            <h2>Stance on National Topics</h2>
            <p>We estimate candidate stance on these topics of national interest for every candidate in our database.</p>
          </div>
          <div className="candidate-stance-table-container">
            <div className="favor-score-header-container">
              <p>Strongly Against</p>
              <p>Against</p>
              <p>Neutral</p>
              <p>In Favor</p>
              <p>Strongly In Favor</p>
            </div>
            <table>
              <tbody>
                {Object.entries(nationalTopicValues).map(([title, field], idx) => (
                  <tr key={title}>
                    <td className="topic-stance" style={idx === Object.entries(nationalTopicValues).length - 1 ? { borderBottom: 0 } : {}}>{title}</td>
                    <td className="topic-value" style={idx === Object.entries(nationalTopicValues).length - 1 ? { borderBottom: 0 } : {}}>
                      <div className="preview-parent">
                        {userNationalStances[field] !== null && userNationalStances[field] !== undefined ? (
                          <>
                            <div
                              className="user-topic-answer candidate-preview-container"
                              data-tip="Where you rated yourself in our quiz"
                              style={{ left: `${generateCandidatePosition(userNationalStances[field], 0)}%` }}
                            >YOU
                            </div>
                            <ReactTooltip />
                          </>
                        ) : null}
                        {candidateStances.map(candidate => (
                          candidate[field] ? (
                            <div
                              className="candidate-preview-container"
                              style={{ left: `${generateCandidatePosition(candidate[field], candidate.randomJiggle)}%` }}
                              onMouseLeave={onCandidateHoverStop}
                              onMouseEnter={() => onCandidateHoverStart(candidate, field, title)}
                              {...overlapContainer ? {} : {
                                'data-tip': candidate.name,
                                onClick: () => {
                                  setSelectedCandidate(candidate.candidateId);
                                  history.push(`/candidate/${candidate.candidateId}?raceId=${raceId}`);
                                },
                              }}
                            >
                              {renderCandidateOverlap(candidate, field, title) || candidate.candidatePreview}
                              <ReactTooltip />
                            </div>
                          ) : null
                        ))}
                        {overlapContainer && overlapContainer.field === field && (
                        <div
                          id="overlap-container"
                          style={{ left: `${27 + ((overlapContainer.candidates[0][field]) * 47)}%` }}
                          onMouseEnter={() => setMouseInOverlapContainer(true)}
                          onMouseLeave={() => {
                            setMouseInOverlapContainer(false);
                            setOverlapContainer();
                          }}
                        >
                          <h4>Overlapping Candidates</h4>
                          <p>We couldn&apos;t distinguish a difference between these candidates&apos; stances on {lowerCaseEachWord(overlapContainer.title)}.</p>
                          <div id="candidate-holder">
                            {overlapContainer.candidates.map(c => (
                              <div
                                className="candidate-overlap-holder"
                                onClick={() => {
                                  setSelectedCandidate(c.candidateId);
                                  history.push(`/candidate/${c.candidateId}?raceId=${raceId}`);
                                }}
                                data-tip={c.name}
                              >{c.candidatePreview}
                              </div>
                            ))}
                            <ReactTooltip />
                          </div>
                        </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {otherTopics.length > 0 ? (
        <div className="candidate-natl-topic-container content-container issue-specific-container">
          <div className="top-info-container">
            <h2>Stance on Topics for {selectedRace?.title}</h2>
            <p>We run a machine learning algorithm to determine topics/issues relevant to this specific race. We’re working on making the topic names more
              intuitive, but for now you may have to infer the meaning.
            </p>
          </div>
          <div className="candidate-stance-table-container">
            <div className="favor-score-header-container">
              <p>Strongly Against</p>
              <p>Against</p>
              <p>Neutral</p>
              <p>In Favor</p>
              <p>Strongly In Favor</p>
            </div>
            <table>
              <tbody>
                {otherTopics.map((title, idx) => (
                  <tr key={title}>
                    <td className="topic-stance" style={idx === otherTopics.length - 1 ? { borderBottom: 0 } : {}}>
                      <div style={{ width: '15px', height: '1px' }} />
                      {capitalizeEachWord(topicMeanings[title] || title || '')}
                      <img
                        src={questionIcon}
                        alt="question icon"
                        className="question-icon"
                        onClick={() => {
                          setModalVisible(true);
                          setCurrFlaggedTopic(title);
                        }}
                      />
                      {flaggedTopics.includes(title)
                        ? <img src={redExclamationIcon} alt="question icon" className="question-icon" onClick={() => { setFlaggedModalVisible(true); setCurrFlaggedTopic(title); }} />
                        : null}
                    </td>
                    <td className="topic-value" style={idx === otherTopics.length - 1 ? { borderBottom: 0 } : {}}>
                      <div className="preview-parent">
                        {userRaceStances[title] !== null && userRaceStances[title] !== undefined ? (
                          <>
                            <div
                              className="user-topic-answer candidate-preview-container"
                              data-tip="Where you rated yourself in our quiz"
                              style={{ left: `${generateCandidatePosition(userRaceStances[title], 0)}%` }}
                            >YOU
                            </div>
                            <ReactTooltip />
                          </>
                        ) : null}
                        {candidateStances.map(candidate => (
                          candidate[title] ? (
                            <div
                              className="candidate-preview-container"
                              style={{ left: `${generateCandidatePosition(candidate[title], candidate.randomJiggle)}%` }}
                              onMouseLeave={onCandidateHoverStop}
                              onMouseEnter={() => onCandidateHoverStart(candidate, undefined, title)}
                              {...overlapContainer ? {} : {
                                'data-tip': candidate.name,
                                onClick: () => {
                                  setSelectedCandidate(candidate.candidateId);
                                  history.push(`/candidate/${candidate.candidateId}?raceId=${raceId}`);
                                },
                              }}
                            >
                              {renderCandidateOverlap(candidate, undefined, title) || candidate.candidatePreview}
                              <ReactTooltip />
                            </div>
                          ) : null
                        ))}
                        {overlapContainer && overlapContainer.title === title && (
                        <div
                          id="overlap-container"
                          style={{ left: `${27 + ((overlapContainer.candidates[0][title]) * 47)}%` }}
                          onMouseEnter={() => setMouseInOverlapContainer(true)}
                          onMouseLeave={() => {
                            setMouseInOverlapContainer(false);
                            setOverlapContainer();
                          }}
                        >
                          <h4>Overlapping Candidates</h4>
                          <p>We couldn&apos;t distinguish a difference between these candidates&apos; stances on
                            {' '}
                            {lowerCaseEachWord(topicMeanings[overlapContainer.title] || overlapContainer.title || '')}.
                          </p>
                          <div id="candidate-holder">
                            {overlapContainer.candidates.map(c => (
                              <div
                                className="candidate-overlap-holder"
                                onClick={() => {
                                  setSelectedCandidate(c.candidateId);
                                  history.push(`/candidate/${c.candidateId}?raceId=${raceId}`);
                                }}
                                data-tip={c.name}
                              >{c.candidatePreview}
                              </div>
                            ))}
                            <ReactTooltip />
                          </div>
                        </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      <Rodal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setCurrFlaggedTopic();
          setCurrFlaggedTopicReason('');
        }}
        height={475}
      >
        <div className="submit-flag-topic-modal">
          <h2>Confused?</h2>
          <p>These topics were extracted by our machine learning algorithm, which might be wrong.</p>
          <p>You can either provide an interpretation of what this topic might mean, or flag this topic as irrelevant to the race.</p>
          <p>Once you submit, our moderators will review your request.</p>
          <br />
          <h4>Original topic phrase: {capitalizeEachWord(currFlaggedTopic || '')}</h4>
          <div className="flag-topic-input-container">
            <h4>Submit a better meaning</h4>
            <input
              placeholder="Interpretation of the topic"
              type="text"
              value={currFlaggedTopicMeaning}
              onChange={event => setCurrFlaggedTopicMeaning(event.target.value)}
              className="flagged-link-reason"
            />
            <button type="button" onClick={submitFlaggedTopicMeaning} className="flag-topic-button" id="submit-flagged-topic-meaning">Submit meaning</button>
          </div>
          <div className="flag-topic-input-container">
            <h4>Tell us why you are confused</h4>
            <input
              placeholder="reason..."
              type="text"
              value={currFlaggedTopicReason}
              onChange={event => setCurrFlaggedTopicReason(event.target.value)}
              className="flagged-link-reason"
            />
            <button type="button" onClick={submitFlaggedTopic} className="flag-topic-button" id="submit-flagged-topic-reason">Flag this topic for review</button>
          </div>
        </div>
      </Rodal>
      {flaggedTopicsForRace
        ? (
          <Rodal
            visible={flaggedModalVisible}
            onClose={() => {
              setFlaggedModalVisible(false);
              setCurrFlaggedTopic();
            }}
            width={450}
          >
            <div className="flagged-topic-modal">
              <h2>Flagged Topic</h2>
              <p>This topic has already been flagged as irrelevant or confusing to this race.</p>
              <p>The machine learning algorithm will re-run the data soon after our moderators review this topic.</p>
              <div>
                Some reasons previous users flagged this topic:
                <ul className="flagged-reason-list">
                  {flaggedTopicsForRace.filter(e => e.topic === currFlaggedTopic).slice(0, 4).map((flaggedTopic) => {
                    return (<li>{`"${flaggedTopic.reason}"`}</li>);
                  })}
                </ul>
              </div>
            </div>
          </Rodal>
        )
        : null
      }

    </div>
  );
};

export default CompareCandidates;
